import type { TrendSeries, TrendSeriesStyle, TrendWidget } from '../../../widgets';
import {
  BindingRow,
  CheckboxField,
  ColourField,
  ListEditor,
  NumberField,
  Row,
  Section,
  SelectField,
  SuggestField,
  TextField,
  patcher,
  type WidgetEditorProps,
} from '../fields';

const STYLES: { value: TrendSeriesStyle; label: string }[] = [
  { value: 'line', label: 'Linie' },
  { value: 'stepAfter', label: 'Treppe' },
  { value: 'area', label: 'Fläche' },
  { value: 'bars', label: 'Balken' },
  { value: 'scatter', label: 'Punkte' },
];

const AGGREGATIONS: { value: NonNullable<TrendWidget['defaultAggregation']>; label: string }[] = [
  // auto and raw both load the shape: all raw points, pixel-identical.
  { value: 'auto', label: 'Automatisch (Rohdaten)' },
  { value: 'raw', label: 'Rohdaten' },
  { value: '1m', label: 'Mittel je Minute' },
  { value: '5m', label: 'Mittel je 5 Minuten' },
  { value: '1h', label: 'Mittel je Stunde' },
  { value: '1d', label: 'Mittel je Tag' },
];

/** Gaengige Zeitraeume als ISO-8601-Dauer. Das ist die Form, in der die
 *  C#-Seite ihre TimeSpan schreibt; ein eigenes Format hier haette einen
 *  Uebersetzer an beiden Enden gebraucht. */
const RANGES = ['PT1H', 'PT6H', 'PT12H', 'PT24H', 'P7D', 'P30D'];

export function TrendEditor(
  { widget, plantId, missing, onChange }: WidgetEditorProps<TrendWidget>,
) {
  const patch = patcher(widget, onChange);
  // Achsengruppen, die es im Widget schon gibt: als Vorschlag, damit zwei
  // Kurven mit derselben Einheit nicht wegen eines Tippfehlers auf zwei
  // Achsen landen.
  const axisGroups = [...new Set(
    widget.series.map((s) => s.axisGroup).filter((g): g is string => !!g),
  )];

  return (
    <>
      <Section title="Zeitraum">
        <Row>
          <SuggestField
            label="Zeitraum beim Öffnen"
            value={widget.defaultRange}
            suggestions={RANGES}
            listId={`trend-range-${widget.id}`}
            placeholder="PT24H"
            hint="ISO-8601-Dauer. PT24H sind 24 Stunden, P7D sieben Tage."
            onChange={(next) => patch({ defaultRange: next })}
          />
          <SelectField
            label="Verdichtung"
            value={widget.defaultAggregation ?? 'auto'}
            options={AGGREGATIONS}
            onChange={(next) => patch({ defaultAggregation: next })}
          />
        </Row>
        <CheckboxField
          label="Mitlaufendes Ende"
          checked={widget.liveTail !== false}
          hint="Neue Messwerte werden angehängt, solange das Widget sichtbar ist."
          onChange={(next) => patch({ liveTail: next })}
        />
      </Section>
      <Section title="Kurven">
        <ListEditor<TrendSeries>
          label="Kurven"
          required
          missing={missing.has('series')}
          items={widget.series}
          addLabel="Kurve"
          emptyHint="Ein Trend ohne Kurve ist eine leere Fläche."
          create={() => ({ point: '', style: 'line' })}
          itemTitle={(s, i) => s.label || s.point || `Kurve ${i + 1}`}
          onChange={(next) => patch({ series: next })}
          renderItem={(series, patchSeries, index) => (
            <>
              <BindingRow
                label="Datenpunkt"
                value={series.point}
                plantId={plantId}
                filter={{ valueType: 'number' }}
                onChange={(next) => patchSeries({ point: next })}
              />
              <Row>
                <TextField
                  label="Beschriftung"
                  value={series.label}
                  placeholder="Vorlauf"
                  onChange={(next) => patchSeries({ label: next })}
                />
                <SelectField
                  label="Form"
                  value={series.style ?? 'line'}
                  options={STYLES}
                  onChange={(next) => patchSeries({ style: next })}
                />
              </Row>
              <Row>
                <ColourField
                  label="Farbe"
                  value={series.colour}
                  onChange={(next) => patchSeries({ colour: next })}
                />
                <SuggestField
                  label="Achsengruppe"
                  value={series.axisGroup}
                  suggestions={axisGroups}
                  listId={`trend-axis-${widget.id}-${index}`}
                  placeholder="leer: nach Einheit gruppieren"
                  hint="Gleicher Name heißt gleiche Y-Achse."
                  onChange={(next) => patchSeries({ axisGroup: next })}
                />
              </Row>
              <Row>
                <NumberField
                  label="Linienstärke"
                  value={series.width}
                  min={0.5}
                  max={6}
                  step={0.25}
                  hint="Vorgabe 1,5"
                  onChange={(next) => patchSeries({ width: next })}
                />
                <NumberField
                  label="Füllung"
                  value={series.fill}
                  min={0}
                  max={1}
                  step={0.05}
                  hint="0 bis 1, zart etwa 0,12"
                  onChange={(next) => patchSeries({ fill: next })}
                />
                <CheckboxField
                  label="Weich"
                  hint="Nur Optik: weiche Kurve statt Treppe. Gilt nicht für den Stil Treppe."
                  checked={series.smooth ?? false}
                  onChange={(next) => patchSeries({ smooth: next || undefined })}
                />
              </Row>
            </>
          )}
        />
      </Section>
    </>
  );
}
